import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Palette, User as UserIcon, Wallet } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useLogoutMutation } from '@/features/auth/api';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeSettingsSheet } from '@/components/theme/ThemeSettingsSheet';

interface HeaderProps {
  username?: string;
  voiceCredits?: string | number;
  ttsCredits?: string | number;
  totalResellers?: number;
  totalEndUsers?: number;
}

export function Header({
  username,
  voiceCredits,
  ttsCredits,
  totalResellers,
  totalEndUsers,
}: HeaderProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const hasStack = useAuthStore((s) => s.hasStack);
  const popSession = useAuthStore((s) => s.popSession);

  const logoutMutation = useLogoutMutation();

  const [themeOpen, setThemeOpen] = useState(false);

  const isSAdmin = role === USER_ROLES.S_ADMIN;
  const isSubUser = role === USER_ROLES.SUB_USER;
  const showBalances = !isSAdmin && !isSubUser;

  const handleLogout = () => {
    if (hasStack) {
      const popped = popSession();
      if (popped) {
        // Server data is now keyed to the previous identity — refetch everything.
        queryClient.clear();
        navigate('/');
        return;
      }
    }
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        queryClient.clear();
        navigate('/login', { replace: true });
      },
    });
  };

  const initials = (username ?? '?')
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:px-6">
        <div className="ml-auto flex items-center gap-4">
          {isSAdmin && (
            <>
              {typeof totalResellers === 'number' && (
                <HeaderStat label="Resellers" value={totalResellers.toLocaleString()} />
              )}
              {typeof totalEndUsers === 'number' && (
                <HeaderStat label="End Users" value={totalEndUsers.toLocaleString()} />
              )}
            </>
          )}
          {showBalances && (
            <>
              <HeaderBalance label="Voice" value={voiceCredits} />
              <HeaderBalance label="TTS" value={ttsCredits} />
            </>
          )}
          {hasStack && (
            <Badge variant="warning" className="hidden md:inline-flex">
              Impersonating
            </Badge>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setThemeOpen(true)}
            aria-label="Open appearance settings"
          >
            <Palette className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 gap-2 px-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {initials || '?'}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden text-sm font-medium md:inline-block">
                  {username ?? 'Account'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{username ?? 'Account'}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/user-profile')}>
                <UserIcon className="h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setThemeOpen(true)}>
                <Palette className="h-4 w-4" />
                Appearance
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="h-4 w-4" />
                {hasStack ? 'Switch back' : 'Logout'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <ThemeSettingsSheet open={themeOpen} onOpenChange={setThemeOpen} />
    </>
  );
}

function HeaderStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="hidden flex-col text-right leading-tight md:flex">
      <span className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

function HeaderBalance({ label, value }: { label: string; value?: string | number }) {
  return (
    <div className="hidden items-center gap-2 rounded-md border bg-card px-3 py-1.5 md:flex">
      <Wallet className="h-4 w-4 text-muted-foreground" />
      <div className="flex flex-col leading-tight">
        <span className="text-2xs uppercase tracking-wider text-muted-foreground">
          {label} Balance
        </span>
        <span className="text-sm font-semibold tabular-nums">₹{value ?? '0'}</span>
      </div>
    </div>
  );
}
